import React, { useState } from "react";
import { useDispatch } from "react-redux";

import Button from "src/components/common/Button";
import { AdminActions } from "src/redux/Admin/actions";

import styles from "./styles.module.scss";

const STATUS_COLORS = {
    Healthy: "#4fd1a5",
    Failed: "#ff6b6b",
    Running: "#f4c95d",
    // Deliberately not run, so neither a success nor a fault: the occurrence a
    // save closed because the report was switched on after it had passed.
    Skipped: "#9aa8b2",
};

const statusColor = (status) => STATUS_COLORS[status] || "#cae1da";

/** A run's raw status ("healthy"/"failed"/"running") shown capitalised. */
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const pad = (n) => String(n).padStart(2, "0");

/**
 * An ISO instant as `YYYY-MM-DD HH:MM:SS KST`. Korea Standard Time is a fixed
 * offset with no daylight saving, so shifting the instant and reading it back in
 * UTC gives Seoul wall-clock time whatever timezone the reader's machine is set
 * to. A dash for a missing time, the raw value when it does not parse.
 */
const fmtKst = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const k = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    return `${k.getUTCFullYear()}-${pad(k.getUTCMonth() + 1)}-${pad(k.getUTCDate())} `
        + `${pad(k.getUTCHours())}:${pad(k.getUTCMinutes())}:${pad(k.getUTCSeconds())} KST`;
};

/**
 * Send Now and View History for one counterparty's user-access report.
 *
 * The report fires per counterparty, so both act on this counterparty alone and
 * read the same `SEND_USER_REPORT:<cpty>` history the scheduled sends write to:
 * a manual send and a weekly one appear in one timeline. `detail` carries the
 * mailer's own words when a send fails, which is the only place a failure is
 * visible at all - a rejected report is otherwise silent.
 *
 * `enabled` is the SAVED state of the report. Both actions read stored config, so
 * with the report off they are disabled rather than hidden, and say why.
 */
const UserReportHistory = ({ cptyCode, enabled }) => {
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dispatch(AdminActions.FetchUserReportHistory(cptyCode));
            setRuns(res?.runs || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const toggle = async () => {
        if (open) {
            setOpen(false);
            return;
        }
        setOpen(true);
        setRuns([]);
        await load();
    };

    const sendNow = async () => {
        setSending(true);
        setMsg(null);
        try {
            await dispatch(AdminActions.RunUserReportNow(cptyCode, "operator sent the report now"));
            setMsg("Sent to the scheduler.");
            if (open) {
                await load();
            }
        } catch (e) {
            setMsg(`Failed: ${e.message}`);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <div className={styles.configRow}>
                <span className={styles.configLabel}>Report</span>
                <Button
                    value={sending ? "Sending…" : "Send Now"}
                    disabled={sending || !enabled}
                    dataTestId="userReport-send-now"
                    onClick={sendNow}
                />
                <Button
                    value={open ? "Hide History" : "View History"}
                    disabled={!enabled}
                    dataTestId="userReport-history"
                    onClick={toggle}
                />
                <span className={styles.configHint}>
                    {enabled
                        ? "Sends this counterparty's report immediately, outside its cadence."
                        : "Enable the report above and save the counterparty to send it."}
                </span>
            </div>
            {msg && (
                <div className={styles.configRow}>
                    <span className={styles.configHint}>{msg}</span>
                </div>
            )}
            {open && (
                <div className={styles.historyPanel}>
                    {loading && <div className={styles.configHint}>Loading…</div>}
                    {error && (
                        <div className={styles.configHint} style={{ color: statusColor("Failed") }}>
                            {error}
                        </div>
                    )}
                    {!loading && !error && runs.length === 0 && (
                        <div className={styles.configHint}>No sends recorded yet.</div>
                    )}
                    {!loading && runs.length > 0 && (
                        <table className={styles.historyTable}>
                            <thead>
                                <tr>
                                    <th>Scheduled for</th>
                                    <th>Ran at</th>
                                    <th>Status</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((run, i) => (
                                    <tr key={i}>
                                        <td>{fmtKst(run.scheduledFor)}</td>
                                        <td>{fmtKst(run.runAt)}</td>
                                        <td style={{ color: statusColor(cap(run.status)), fontWeight: 600 }}>
                                            {cap(run.status)}
                                        </td>
                                        <td className={styles.historyDetail}>{run.detail || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </>
    );
};

export default UserReportHistory;
